import { MouseEvent, useState, Dispatch, SetStateAction, useCallback, useMemo } from 'react'

import { MatchingFile, MatchingPaymentTicket, matchingCapabilities } from '../../core/_models'
import { KTIcon } from '../../../../../_metronic/helpers'
import { convertUTCtoMSK, isVirtualMerchant, formatSum, formatRuPhoneE164 } from '../../../../../utils'
import { ChangeExpectedAmountMatchingPaymentTicket } from '../../core/_requests'
import Swal from 'sweetalert2'
import { getErrorStatus, getErrorText } from '../../../../common/api/errors'
import { useDepositTicketActions } from '../common/useDepositTicketActions'
import { EFileBadgeStatus, EFileStatus, EMatchingPaymentTicketStatus } from '../../../../../types/enums'
import { useIntl } from 'react-intl'
import { DisputeBadge } from '../common/DisputeBadge'
import { ActivePaymentTicketRowMenu } from './ActivePaymentTicketRowMenu'
import { ReleaseDepositChunkModal } from '../common/ReleaseDepositChunkModal'
import disputeRowStyles from './DisputeRow.module.css'
import { DisputeModal } from '../../routes/Disputes'
import { ClientCell } from '../../../../common/ClientCell'
import { AmountInputCell } from '../common/AmountInputCell'
import { AmountChangeBadge } from '../../../../../components/AmountChangeBadge'
import { AntifraudSignal } from '../common/AntifraudSignal'
import { ReceiptGateBadge } from '../common/ReceiptGateBadge'
import { ReceiptCell } from '../common/ReceiptCell'
import { receiptDecisionOpen } from '../../core/receipts'
import { ROLE, UserModel } from '../../../auth'
import { IsGranted } from '../../../auth/PrivateRoute'
import { MatchingTicketLink } from '../../../../common/MatchingTicketLink'


interface Props {
  ticket: MatchingPaymentTicket
  setShowAlert: Dispatch<SetStateAction<boolean>>
  currentUser: UserModel | undefined
  updateBillStatus: (
    token: string,
    billId: MatchingFile['id'],
    status: MatchingFile['status']
  ) => void
  removeTicketFromList: (token: string) => void
  updateBillForTicket: (
    token: string,
    bill: MatchingFile
  ) => void
  updateExpectedAmount: (token: string, amount: number, requisiteAmount?: number | null) => void
  visibility: Record<string, boolean>
  visibleColumns: string[]
  canManageBills: boolean
  canManageTickets: boolean
  canCreateDispute: boolean
  markTicketDisputed: (token: string) => void
}


const ActivePaymentTicketItem = ({
  ticket,
  setShowAlert,
  currentUser,
  updateBillStatus,
  removeTicketFromList,
  updateBillForTicket,
  updateExpectedAmount,
  visibility,
  visibleColumns,
  canManageBills,
  canManageTickets,
  canCreateDispute,
  markTicketDisputed,
}: Props) => {
  const intl = useIntl()
  const [showDisputeModal, setShowDisputeModal] = useState(false)
  const [showReleaseChunkModal, setShowReleaseChunkModal] = useState(false)
  const [chunkReleased, setChunkReleased] = useState(false)
  const [showTooltip, setShowTooltip] = useState<boolean>(false)

  const virtualMerchant = isVirtualMerchant(ticket.merchantId);

  // Ручная переотправка КБ — отдельное право (независимо от matching-прав canManageTickets:
  // matching-оператор её не видит). Список ролей держим в matchingCapabilities, он обязан
  // совпадать с бэкендом.
  const canResend = IsGranted(matchingCapabilities.canResendCallback, currentUser)

  const onReceiptUploaded = useCallback(
    (file: MatchingFile) => updateBillForTicket(ticket.ticketToken, file),
    [updateBillForTicket, ticket.ticketToken]
  )
  const onReceiptDecided = useCallback(
    (billId: MatchingFile['id'], status: EFileStatus) =>
      updateBillStatus(ticket.ticketToken, billId, status),
    [updateBillStatus, ticket.ticketToken]
  )
  // Решение по чеку выводит тикет из активной очереди.
  const onReceiptDecisionAcknowledged = useCallback(
    () => removeTicketFromList(ticket.ticketToken),
    [removeTicketFromList, ticket.ticketToken]
  )

  // Завершение и отмена вынесены в общий хук: ту же операцию зовут обе карточки тикета, и второй
  // путь к ней разошёлся бы в текстах, разборе кодов ответа и правах.
  const { complete: handleFinishTicket, decline: handleDeclineTicket } = useDepositTicketActions({
    token: ticket.ticketToken,
    amount: ticket.requisiteAmount ?? ticket.expectedAmount,
    currentUser,
    enabled: canManageTickets,
    onDone: () => removeTicketFromList(ticket.ticketToken),
  })

  const handleCopyToClipboard = (e: MouseEvent<HTMLSpanElement>) => {
    if (!ticket.requisite) return
    navigator.clipboard.writeText(formatRuPhoneE164(ticket.requisite.identifier))
    setShowAlert(true)
    setTimeout(() => {
      setShowAlert(false)
    }, 1400)
  }

  /** @todo: отрефакторить это и вынести в компонент */
  const getRequisiteTooltip = useCallback(() => {
    if (!ticket?.requisite) return null
    let tooltip = ''
    const { cardholder, bank } = ticket.requisite || {}
    if (cardholder) tooltip += `Владелец: ${cardholder}`
    if (bank) tooltip += tooltip ? `, Банк: ${bank}` : `Банк: ${bank}`
    return tooltip || null
  }, [ticket?.requisite])


  const [fetchingExpectedAmount, setFetchingExpectedAmount] = useState(false)
  
  // Изменение запрошенной суммы
  const handleChangeExpectedAmount = useCallback(async (amount: number) => {
    if (!canManageTickets) return
    setFetchingExpectedAmount(true)
    try {
      const response = await ChangeExpectedAmountMatchingPaymentTicket(
        ticket.ticketToken,
        parseFloat(amount.toString()),
        currentUser
      )
      if (response.data?.success && response.data.ticket) {
        updateExpectedAmount(ticket.ticketToken, amount, response.data.ticket.requisiteAmount)
      }
    } catch (err: unknown) {
      Swal.fire({
        title: '',
        text:
          getErrorStatus(err) === 422
            ? 'Тикет не может быть изменен'
            : getErrorText(err, 'Невозможно изменить сумму тикета', { withFieldName: false }),
        icon: 'warning',
      })
    } finally {
      setFetchingExpectedAmount(false)
    }
  }, [ticket, currentUser, updateExpectedAmount, canManageTickets])

  return (
    <>
    <tr className={ticket.disputeStatus === 'OPEN' ? disputeRowStyles.disputeRow : undefined}>
      {visibility.ticketToken && (
        <td className='w-300px'>
          <p className='mb-0'><MatchingTicketLink token={ticket.ticketToken} /></p>
          {ticket.concierge && (
            <div className='mt-1'>
              <span className='badge badge-light-primary'>Консьерж</span>
            </div>
          )}
          {ticket.disputeStatus === 'OPEN' && (
            <div className='mt-1'>
              {/* CPAYMENT-1690: журнал открыт и оператору платежей мерчанта — иначе ссылка была бы
                  мёртвой ровно у того, кто этот диспут и завёл. */}
              <DisputeBadge ticketToken={ticket.ticketToken} linkable={IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR], currentUser)} />
            </div>
          )}
          {/* Связанная выплата приходит в том же ответе списка — оператору не нужно искать её
              отдельно. Подпись обязательна: два UUID подряд без неё неразличимы. */}
          {/* A compensation payout is named as such in the same caption — colour follows the
              overpayment colour used in the payout lists. */}
          {!!ticket.relatedPayoutTicket?.token && (
            <div className={`mt-2 ${ticket.relatedPayoutTicket.isOverpaymentCompensation ? 'text-info' : 'text-muted'}`}>
              <p className='fw-bold fs-9 mb-0'>
                {ticket.relatedPayoutTicket.isOverpaymentCompensation ? 'Компенсация переоплаты:' : 'Тикет выплаты:'}
              </p>
              {/* Внутри мэтчинга выплата ведёт на свою мэтчинговую карточку; мерчант рядом — чтобы
                  опознать выплату, не открывая её. */}
              <span className='fs-9'>
                <MatchingTicketLink token={ticket.relatedPayoutTicket.token} type='payout' />
              </span>
              {ticket.relatedPayoutTicket.merchant && (
                <span className='fs-9 ms-2' data-testid='related-payout-merchant'>
                  · {ticket.relatedPayoutTicket.merchant}
                </span>
              )}
            </div>
          )}
        </td>
      )}
      {visibility.transactionId && <td className='w-200px'>{ticket.merchantTransactionId || '-'}</td>}
      {visibility.createdAt && <td>{convertUTCtoMSK(ticket.createdAt)}</td>}
      {visibility.amount && (!virtualMerchant ? <AmountInputCell initialAmount={ticket.amount} requisiteAmount={ticket.requisiteAmount} expectedAmount={ticket.expectedAmount} onAccept={handleChangeExpectedAmount} fetching={fetchingExpectedAmount} isUnlimChange={IsGranted([ROLE.ROLE_ADMIN], currentUser)} disabled={ticket.disputeStatus === 'OPEN' || ticket.status === EMatchingPaymentTicketStatus.FILES_AUTO_VERIFICATION} footer={
          <AmountChangeBadge
            requestedAmount={ticket.expectedAmount}
            issuedAmount={ticket.requisiteAmount ?? null}
            source={ticket.amountChangeSource ?? null}
          />
        }/> : <td>
          {/* Виртуальный мерчант: поле ввода не показываем, но сумму к переводу и пометку — да. */}
          <span>{formatSum(ticket.requisiteAmount ?? ticket.expectedAmount ?? 0)}</span>
          <AmountChangeBadge
            requestedAmount={ticket.expectedAmount}
            issuedAmount={ticket.requisiteAmount ?? null}
            source={ticket.amountChangeSource ?? null}
          />
        </td>)}
      {visibility.user && <ClientCell externalId={ticket.externalUserId} token={ticket.clientToken} paymentTicketToken={ticket.ticketToken} />}
      {visibility.merchant && <td>{ticket.merchant || '-'}</td>}
      {visibility.paymentMethod && (
        <td
          className='cursor-pointer position-relative'
          onClick={handleCopyToClipboard}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {showTooltip && getRequisiteTooltip() && (
            <div
              style={{
                zIndex: 20,
                position: 'absolute',
                top: '5px',
                left: '10px',
              }}
              className='badge bg-success text-white'
            >
              <p className='mb-0'>{getRequisiteTooltip()}</p>
            </div>
          )}
          <span>{ticket.requisite?.identifier ? formatRuPhoneE164(ticket.requisite?.identifier) : '-'}</span>
          {/* Метод известен с создания тикета, реквизит — нет: подпись рисуется и у строки, где
              реквизит ещё не выдан. Названия нет — блока нет, прочерк здесь не ставим: он занял бы
              строку под реквизитом и читался бы как «метода нет». */}
          {!!ticket.paymentMethod && (
            <div className='fs-9 text-muted'>{ticket.paymentMethod}</div>
          )}
        </td>
      )}
      {visibility.files && (
        <td className='text-center'>
          <ReceiptCell
            ticketToken={ticket.ticketToken}
            files={ticket.files}
            merchantId={ticket.merchantId}
            canManageBills={canManageBills}
            showDecisionBadge
            canDecide={canManageBills && ticket.disputeStatus !== 'OPEN' && receiptDecisionOpen(ticket.status)}
            onUploaded={onReceiptUploaded}
            onDecided={onReceiptDecided}
            onDecisionAcknowledged={onReceiptDecisionAcknowledged}
            /* CPAYMENT-1616: чек прошёл гейт без подтверждения — причина в тултипе. */
            extra={<ReceiptGateBadge gate={ticket.receiptGate} />}
          />
        </td>
      )}
      {visibility.antifraud && (
        <td className='text-center'>
          <AntifraudSignal signal={ticket.antifraudSignal} />
        </td>
      )}
      <td className='text-end'>
        <div className='d-flex justify-content-end align-items-center gap-2'>
          {canManageTickets && (
            <ActivePaymentTicketItemTicketActions
              ticket={ticket}
              handleFinishTicket={handleFinishTicket}
              handleDeclineTicket={handleDeclineTicket}
            />
          )}
          <ActivePaymentTicketRowMenu
            ticketToken={ticket.ticketToken}
            canResend={canResend}
            canCreateDispute={
              canCreateDispute &&
              ticket.status === EMatchingPaymentTicketStatus.FILES_VERIFICATION &&
              ticket.disputeStatus !== 'OPEN'
            }
            onCreateDispute={() => setShowDisputeModal(true)}
            canReleaseChunk={ticket.canReleaseChunk === true && !chunkReleased}
            onReleaseChunk={() => setShowReleaseChunkModal(true)}
          />
        </div>
      </td>
    </tr>
    {showDisputeModal && (
      <DisputeModal
        show={showDisputeModal}
        onClose={() => setShowDisputeModal(false)}
        context={{
          paymentTicketToken: ticket.ticketToken,
          // amount у тикета в FILES_VERIFICATION ещё null. CPAYMENT-1669: спор идёт о сумме,
          // которую клиенту выдали, поэтому рядом с запросом показываем и сумму к переводу.
          expectedAmount: ticket.expectedAmount ?? null,
          requisiteAmount: ticket.requisiteAmount ?? null,
          currency: 'RUB',
        }}
        onCreated={(_record, resolution) => {
          setShowDisputeModal(false)
          if (resolution === 'NONE') {
            // Строка мгновенно становится замороженной: подсветка, бейдж, снятые действия с чеком.
            markTicketDisputed(ticket.ticketToken)
            return
          }
          // Resolved at creation: the ticket has left receipt verification.
          removeTicketFromList(ticket.ticketToken)
        }}
      />
    )}
    {showReleaseChunkModal && (
      <ReleaseDepositChunkModal
        show={showReleaseChunkModal}
        onClose={() => setShowReleaseChunkModal(false)}
        depositToken={ticket.ticketToken}
        heldUntil={ticket.chunkHeldUntil ?? null}
        // Список активных пополнений сам не перезапрашивается, поэтому состояние снимаем
        // локально. Отложенное освобождение при этом остаётся отзываемым — пункт вернётся
        // после ближайшего обновления списка, если момент ещё не наступил.
        onReleased={() => setChunkReleased(true)}
      />
    )}
    </>
  )
}

export default ActivePaymentTicketItem

const ActivePaymentTicketItemTicketActions = ({
  ticket,
  handleFinishTicket,
  handleDeclineTicket,
}: {
  ticket: MatchingPaymentTicket
  handleFinishTicket: () => void
  handleDeclineTicket: () => void
}) => {
  // CPAYMENT-1616: в статусах проверки чека ручное завершение/отклонение тикета недоступно
  // (в workflow из них есть только verify/reject чека) — прячем кнопки, как и для FILES_VERIFICATION.
  if (
    ticket.status === EMatchingPaymentTicketStatus.FILES_VERIFICATION ||
    ticket.status === EMatchingPaymentTicketStatus.FILES_AUTO_VERIFICATION
  ) {
    return null
  }

  return (
    <>
      <button
        className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1 me-4'
        onClick={handleFinishTicket}
        title={`Завершить тикет ${ticket.ticketToken}\nСумма к переводу: ${Number(ticket.requisiteAmount || ticket.expectedAmount || 0).toFixed(2)}`}
      >
        <KTIcon className='fs-1' iconName='check-circle' />
      </button>
      <button
        className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1 fs-6'
        onClick={handleDeclineTicket}
        title={`Отменить тикет ${ticket.ticketToken}`}
      >
        <KTIcon className='fs-1' iconName='cross-circle' />
      </button>
    </>
  )
}
